import type {
  AccessView,
  CollectionDetail,
  CheckoutSessionView,
  HobbyCatalog,
  Overview,
  ReferralView,
  ReferralRewardView,
  MonthlyReportView,
  RegularityRewardView,
  SubscriptionView,
  TaskView,
  TaskDetail,
  UserProfile
} from "./types";

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
let accessToken = "";

interface ApiEnvelope<T> {
  data: T;
  requestId: string;
}

interface ErrorEnvelope {
  error: { code: string; message: string };
  requestId: string;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly requestId?: string
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (accessToken) headers.set("authorization", `Bearer ${accessToken}`);

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const body = await response.json() as ApiEnvelope<T> | ErrorEnvelope;
  if (!response.ok || "error" in body) {
    const error = "error" in body ? body.error : { code: "REQUEST_FAILED", message: "Не удалось загрузить данные" };
    throw new ApiError(error.code, error.message, response.status, body.requestId);
  }
  return body.data;
}

export async function createSession(initData: string, timezone: string) {
  const localAuthEnabled = import.meta.env.VITE_ENABLE_LOCAL_AUTH === "true";
  if (!API_BASE && !localAuthEnabled) {
    throw new ApiError(
      "API_BASE_MISSING",
      "Приложение временно недоступно. Закройте его и откройте заново из бота.",
      503
    );
  }
  const session = initData
    ? await request<{ accessToken: string; expiresIn: number }>("/api/v1/session/exchange", {
        method: "POST",
        body: JSON.stringify({ initData, timezone })
      })
    : localAuthEnabled
      ? await request<{ accessToken: string; expiresIn: number }>("/api/v1/dev/session", {
          method: "POST",
          body: JSON.stringify({
            telegramUserId: Number(import.meta.env.VITE_LOCAL_TELEGRAM_USER_ID) || 900000001,
            firstName: "Локальный пользователь",
            timezone
          })
        })
      : (() => { throw new ApiError("TELEGRAM_REQUIRED", "Откройте Mini App из Telegram", 401); })();
  accessToken = session.accessToken;
  return session;
}

export const api = {
  catalog: () => request<HobbyCatalog>("/api/v1/hobbies"),
  me: () => request<UserProfile>("/api/v1/me"),
  acceptLegal: () => request<{ accepted: true; termsVersion: string; privacyVersion: string }>("/api/v1/me/legal/accept", { method: "POST" }),
  overview: () => request<Overview>("/api/v1/me/overview"),
  accesses: async () => (await request<{ accesses: AccessView[] }>("/api/v1/me/accesses")).accesses,
  activeTasks: async () => (await request<{ tasks: TaskView[] }>("/api/v1/tasks/active")).tasks,
  collection: async () => (await request<{ items: TaskView[] }>("/api/v1/collection")).items,
  history: async () => (await request<{ items: TaskView[] }>("/api/v1/history")).items,
  collectionDetail: (taskId: string) => request<CollectionDetail>(`/api/v1/collection/${taskId}`),
  taskDetail: (taskId: string) => request<TaskDetail>(`/api/v1/tasks/${taskId}`),
  activateFree: (hobbyId: string, timezone: string) => request("/api/v1/hobbies/" + hobbyId + "/free-activate", {
    method: "POST",
    body: JSON.stringify({ timezone })
  }),
  changeFree: (hobbyId: string, timezone: string) => request("/api/v1/hobbies/" + hobbyId + "/free-change", {
    method: "POST",
    body: JSON.stringify({ timezone })
  }),
  createCheckout: (hobbyId: string, timezone: string) => request<CheckoutSessionView>(`/api/v1/hobbies/${hobbyId}/checkout`, {
    method: "POST",
    body: JSON.stringify({ timezone })
  }),
  trackPaywallView: (hobbyId: string) => request<{ tracked: boolean }>(`/api/v1/hobbies/${hobbyId}/paywall-view`, { method: "POST" }),
  markCheckoutOpened: (checkoutSessionId: string) => request<{ tracked: boolean }>(`/api/v1/checkouts/${checkoutSessionId}/opened`, { method: "POST" }),
  checkoutStatus: (checkoutSessionId: string) => request<{ status: string; hobbyId: string }>(`/api/v1/checkouts/${checkoutSessionId}`),
  notifyNewHobbies: () => request<{ enabled: true; alreadyEnabled: boolean }>("/api/v1/me/notifications/new-hobbies", { method: "POST" }),
  referral: () => request<ReferralView>("/api/v1/me/referral", { method: "POST" }),
  referralRewards: async () => (await request<{ rewards: ReferralRewardView[] }>("/api/v1/me/referral-rewards")).rewards,
  activateReferralReward: (rewardId: string, hobbyId: string) => request<{ endsAt: string; taskId: string }>(`/api/v1/referral-rewards/${rewardId}/activate/${hobbyId}`, { method: "POST" }),
  monthlyReports: () => request<{ reports: MonthlyReportView[]; rewards: RegularityRewardView[] }>("/api/v1/me/monthly-reports"),
  activateRegularityReward: (awardId: string, hobbyId: string) => request<{ endsAt: string; taskId: string }>(`/api/v1/regularity-rewards/${awardId}/activate/${hobbyId}`, { method: "POST" }),
  subscriptions: async () => (await request<{ subscriptions: SubscriptionView[] }>("/api/v1/me/subscriptions")).subscriptions,
  cancelSubscription: (subscriptionId: string, acceptRetention: boolean) => request<{ status: string; accessUntil: string; retentionAccepted: boolean }>(`/api/v1/subscriptions/${subscriptionId}/cancel`, {
    method: "POST",
    body: JSON.stringify({ acceptRetention })
  }),
  reenableSubscription: (subscriptionId: string) => request<{ status: string; autoRenewEnabled: true }>(`/api/v1/subscriptions/${subscriptionId}/re-enable`, { method: "POST" }),
  startSupport: () => request<{ ready: true }>("/api/v1/me/support/start", { method: "POST" }),
  startDataRequest: () => request<{ ready: true }>("/api/v1/me/data-request/start", { method: "POST" }),
  returnToTelegram: (taskId: string, responseId?: string) => request<{ delivered: boolean }>(`/api/v1/tasks/${taskId}/return-to-telegram`, {
    method: "POST",
    body: JSON.stringify(responseId ? { responseId } : {})
  }),
  updateTimezone: (timezone: string) => request("/api/v1/me/timezone", {
    method: "PUT",
    body: JSON.stringify({ timezone })
  })
};
